import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { Button, cn } from '@ideoz/ui';
import { StatusBadge } from './StatusBadge';
import type { Integration } from '../data/catalog';

interface IntegrationCardProps {
  integration: Integration;
  connected: boolean;
  busy?: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  /** When provided (tool has setup instructions), show a "How to connect" help button. */
  onShowInstructions?: () => void;
}

/**
 * Grid card in the integrations list. Connect / Disconnect happen inline. The
 * card body is not clickable — the detail (permissions) route still exists but
 * is not reachable from the card.
 */
export const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  connected,
  busy = false,
  onConnect,
  onDisconnect,
  onShowInstructions,
}) => {
  const { t } = useTranslation();
  const Icon = integration.icon;
  const cat = t(`integrations.catalog.${integration.id}.category`, {
    defaultValue: integration.category,
  });
  const summary = t(`integrations.catalog.${integration.id}.summary`, {
    defaultValue: integration.summary,
  });
  const connectLabel = t(`integrations.catalog.${integration.id}.connectLabel`, {
    defaultValue: integration.connectLabel,
  });

  return (
    <div
      data-testid={`integration-card-${integration.id}`}
      className={cn(
        'flex w-full flex-col rounded-2xl border border-gray-200 bg-white p-5 text-start transition-colors',
        'dark:border-gray-700 dark:bg-ink-surface'
      )}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          <Icon size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-gray-900 dark:text-gray-100">
              {integration.name}
            </span>
            <StatusBadge connected={connected} />
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500">{cat}</p>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">{summary}</p>

      <div className="mt-5 flex items-center gap-2">
        {connected ? (
          <Button
            variant="destructive"
            size="xs"
            onClick={onDisconnect}
            disabled={busy}
            data-testid={`integration-disconnect-${integration.id}`}
          >
            {t('integrations.disconnect')}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="xs"
            onClick={onConnect}
            disabled={busy}
            data-testid={`integration-connect-${integration.id}`}
          >
            {connectLabel}
          </Button>
        )}
        {onShowInstructions && (
          <Button
            variant="outline"
            size="xs"
            onClick={onShowInstructions}
            data-testid={`integration-instructions-${integration.id}`}
            className="inline-flex items-center gap-1.5"
          >
            <HelpCircle size={15} />
            {t('integrations.instructions.cta')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default IntegrationCard;
